/**
 * Das Datenmodell einer Aufnahme nach der Bodenkundlichen Kartieranleitung (KA).
 *
 * Es folgt dem amtlichen Aufnahmeformblatt KA 6 (frei herunterladbar bei den
 * Staatlichen Geologischen Diensten, www.infogeo.de/KA6): Titeldaten,
 * Aufnahmesituation, horizontbezogene Daten I und II, Profilkennzeichnung.
 * Welches Feld des Bogens wo im Modell liegt, steht in `ka-felder.ts`.
 *
 * **Der Unterschied zur SEA-Aufnahme:** Die SEA führt Substratfolge und
 * Horizontfolge als zwei Tabellen mit eigenen Tiefen. Die KA führt EINE Tabelle:
 * je Horizont eine Zeile, die alles trägt (Merkmale, Bodenart, Gestein, Symbol).
 *
 * **Was NICHT hier liegt:** Nummer, Datum, Bearbeiter, Koordinaten, Aufnahmetiefe
 * und Bemerkungen bleiben an ihrer bisherigen Stelle im Punkt (`header…`). Karte,
 * Listen, Suche, Gastmodus und Workflow lesen sie dort, und zwar für jedes
 * Verfahren gleich.
 *
 * **Diese Datei enthält keine Schlüssellisten.** Sie kennt nur den Aufbau, nicht
 * die zulässigen Werte (siehe `ka-listen.ts` zur Rechtefrage).
 */

import { KaAuflage, KA_AUFLAGE_VORGABE } from '../verfahren';

/**
 * Ein eingetragener Wert: ein Kurzzeichen, ein Text oder eine Zahl.
 * Der leere Text heißt "noch nicht ausgefüllt".
 */
export type KaEintrag = string | number;

/**
 * Die beiden Kennzeichen, die der amtliche Bogen auf jeder Seite nennt:
 * "nb = nicht bestimmt, nv = nicht vorhanden".
 *
 * Das sind zwei verschiedene Auskünfte, und ein leeres Feld ist eine dritte:
 * nicht vorhanden = nachgesehen, da ist nichts. Nicht bestimmt = nicht
 * nachgesehen. Leer = noch nicht ausgefüllt. Wer sie zusammenwirft, kann
 * hinterher nicht mehr sagen, ob ein Merkmal fehlt oder nur niemand hingesehen hat.
 */
export const KA_NICHT_VORHANDEN = 'nv';
export const KA_NICHT_BESTIMMT = 'nb';

export type KaZustand = 'leer' | 'nv' | 'nb' | 'wert';

/** In welchem der vier Zustände ist dieses Feld? Bei Feldern mit mehreren Einträgen zählt die ganze Liste. */
export function kaZustand(wert: unknown): KaZustand {
	if (Array.isArray(wert)) {
		const gefuellt = wert.filter(w => kaZustand(w) !== 'leer');
		if (gefuellt.length === 0) return 'leer';
		if (gefuellt.length === 1) return kaZustand(gefuellt[0]);
		return 'wert';
	}
	if (wert === undefined || wert === null) return 'leer';
	if (typeof wert === 'string') {
		const t = wert.trim();
		if (t === '') return 'leer';
		if (t === KA_NICHT_VORHANDEN) return 'nv';
		if (t === KA_NICHT_BESTIMMT) return 'nb';
	}
	return 'wert';
}

/** Titeldaten, soweit sie nicht schon im Kopf des Punktes stehen. */
export interface KaTitel {
	tkNr: KaEintrag;
	projektNr: KaEintrag;
	hoeheNhn: KaEintrag;
	/**
	 * Feld 9 des Bogens, "Aufschlussart/Intensität". Ein ZUSÄTZLICHES Feld: Die
	 * Punktart des Punktes (`type`) mit ihrem Kartensymbol bleibt daneben bestehen
	 * und wird auch in KA-Projekten aus der vorhandenen Punktarten-Liste gewählt
	 * (Nutzer-Entscheidung 20.09.2026). Eigene Punktarten für KA-Projekte gibt es nicht.
	 */
	aufschlussart: KaEintrag;
}

/** Aufnahmesituation (Felder 12 bis 25 des Bogens). */
export interface KaSituation {
	hangneigung: KaEintrag;
	exposition: KaEintrag;
	reliefformtyp: KaEintrag;
	lageImRelief: KaEintrag;
	mikrorelief: KaEintrag;
	bodenabtragAuftragNatuerlich: KaEintrag;
	bodenabtragAuftragKuenstlich: KaEintrag;
	nutzungsart: KaEintrag;
	vegetation: KaEintrag;
	witterung: KaEintrag;
	anthropogeneVeraenderungen: KaEintrag;
	bodenoberflaeche: KaEintrag;
	versiegelungsart: KaEintrag;
	regenwuermer: KaEintrag;
	bemerkungen: KaEintrag;
}

/**
 * Eine Zeile der Horizonttabelle (horizontbezogene Daten I und II).
 *
 * Felder mit `KaEintrag[]` tragen mehrere Einträge je Horizont. Der Bogen sieht
 * dafür je Horizont drei Schreibzeilen vor. Welche Felder das sind, ist eine
 * Arbeitsannahme (siehe `ka-felder.ts`) und am Buch zu prüfen.
 */
export interface KaHorizont {
	/** Kennung der Zeile, wie bei Substrat- und Horizontzeilen: damit sich dieselbe Zeile in zwei Fassungen wiederfinden lässt. */
	id: string;

	// Horizontbezogene Daten I
	obergrenze: KaEintrag;
	untergrenze: KaEintrag;
	grenze: KaEintrag;
	farbe: KaEintrag;
	humusgehalt: KaEintrag;
	bodenfeuchte: KaEintrag;
	konsistenz: KaEintrag;
	oxidationsmerkmale: KaEintrag[];
	reduktionsmerkmale: KaEintrag[];
	weitereMerkmale: KaEintrag[];
	gefuegeform: KaEintrag;
	lagerungsart: KaEintrag;
	lagerungsform: KaEintrag;
	verfestigungsgrad: KaEintrag;
	hohlraeume: KaEintrag[];
	zersetzungsstufe: KaEintrag;
	durchwurzelungsintensitaet: KaEintrag;
	wurzelverteilung: KaEintrag;
	pilzmycel: KaEintrag;
	packungsdichte: KaEintrag;
	/** Steht im KA6-Bogen am ENDE der Tabelle (Feld 58): erst beschreiben, dann benennen. */
	horizontsymbol: KaEintrag;

	// Horizontbezogene Daten II
	feinbodenart: KaEintrag;
	grobbodenfraktionen: KaEintrag[];
	summeGrobboden: KaEintrag;
	grobbodenanbindung: KaEintrag;
	carbonatgehalt: KaEintrag;
	/** Im Bogen "Geog. org. Kohlenstoff". Die Abkürzung ist hier bewusst nicht ausgeschrieben. */
	geogOrgKohlenstoff: KaEintrag;
	bodenausgangsgestein: KaEintrag;
	geogenese: KaEintrag;
	periglaziaereLage: KaEintrag;
	stratigraphie: KaEintrag;
	grobkomponenten: KaEintrag[];
	feinkomponenten: KaEintrag[];
	beimengungen: KaEintrag[];
	besondereStrukturen: KaEintrag[];
	geruch: KaEintrag;
	bemerkungen: KaEintrag;
	/** Steht im KA6-Bogen am ENDE der Tabelle (Feld 59), wie das Horizontsymbol. */
	substratart: KaEintrag;
	probenGestoert: KaEintrag;
	probenUngestoert: KaEintrag;
}

/** Profilkennzeichnung (Felder 60 bis 71 des Bogens). */
export interface KaProfil {
	bodensystematischeEinheit: KaEintrag;
	substratsystematischeEinheit: KaEintrag;
	bodenausgangsgesteinsfolge: KaEintrag;
	humusform: KaEintrag;
	hydrogenetischerMoortyp: KaEintrag;
	durchwurzelbarerBodenraum: KaEintrag;
	wasserstand: KaEintrag;
	grundnaessestufe: KaEintrag;
	besondereWasserverhaeltnisse: KaEintrag;
	stauHaftnaessestufe: KaEintrag;
	erosionsgrad: KaEintrag;
	bemerkungen: KaEintrag;
}

/** Die ganze KA-Aufnahme, wie sie als `ka` am Punkt hängen wird. */
export interface KaAufnahme {
	auflage: KaAuflage;
	titel: KaTitel;
	situation: KaSituation;
	horizonte: KaHorizont[];
	profil: KaProfil;
}

/**
 * Eine leere Horizontzeile. Die Kennung kommt vom Aufrufer (`neueZeilenKennung()`
 * aus dem Datenmodell), damit diese Datei ohne Karte und ohne Bibliothek auskommt.
 *
 * Jedes Feld steht ausdrücklich da, leer: So ist die Zeile in der Datenbank und
 * im Vergleich zweier Fassungen vollständig, und `ka-felder.spec.ts` kann prüfen,
 * dass jedes Feld des Bogens im Modell wirklich einen Platz hat.
 */
export function neuerKaHorizont(id: string): KaHorizont {
	return {
		id,
		obergrenze: '', untergrenze: '', grenze: '', farbe: '',
		humusgehalt: '', bodenfeuchte: '', konsistenz: '',
		oxidationsmerkmale: [], reduktionsmerkmale: [], weitereMerkmale: [],
		gefuegeform: '', lagerungsart: '', lagerungsform: '', verfestigungsgrad: '',
		hohlraeume: [], zersetzungsstufe: '',
		durchwurzelungsintensitaet: '', wurzelverteilung: '', pilzmycel: '',
		packungsdichte: '', horizontsymbol: '',
		feinbodenart: '', grobbodenfraktionen: [], summeGrobboden: '',
		grobbodenanbindung: '', carbonatgehalt: '', geogOrgKohlenstoff: '',
		bodenausgangsgestein: '', geogenese: '', periglaziaereLage: '', stratigraphie: '',
		grobkomponenten: [], feinkomponenten: [], beimengungen: [], besondereStrukturen: [],
		geruch: '', bemerkungen: '', substratart: '',
		probenGestoert: '', probenUngestoert: '',
	};
}

/** Eine leere KA-Aufnahme ohne Horizonte. */
export function leereKaAufnahme(auflage: KaAuflage = KA_AUFLAGE_VORGABE): KaAufnahme {
	return {
		auflage,
		titel: { tkNr: '', projektNr: '', hoeheNhn: '', aufschlussart: '' },
		situation: {
			hangneigung: '', exposition: '', reliefformtyp: '', lageImRelief: '', mikrorelief: '',
			bodenabtragAuftragNatuerlich: '', bodenabtragAuftragKuenstlich: '',
			nutzungsart: '', vegetation: '', witterung: '', anthropogeneVeraenderungen: '',
			bodenoberflaeche: '', versiegelungsart: '', regenwuermer: '', bemerkungen: '',
		},
		horizonte: [],
		profil: {
			bodensystematischeEinheit: '', substratsystematischeEinheit: '',
			bodenausgangsgesteinsfolge: '', humusform: '', hydrogenetischerMoortyp: '',
			durchwurzelbarerBodenraum: '', wasserstand: '', grundnaessestufe: '',
			besondereWasserverhaeltnisse: '', stauHaftnaessestufe: '', erosionsgrad: '',
			bemerkungen: '',
		},
	};
}
